"use client";

import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import logger from "@/lib/logger";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useCookie } from "../useCookie";
import { formatMarkdown } from "@/components/formatMarkdown";
import Link from "next/link";

// Define prop types
export interface AnalyticsConsentProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "small";
  onAccept?: () => void;
  onDecline?: () => void;
  onConsent?: (consent: Date | false) => void;
  description?: string;
  learnMoreHref?: string;
}

export const AnalyticsConsent = forwardRef<HTMLDivElement, AnalyticsConsentProps>(
  (
    {
      variant: initialVariant = "default",
      onAccept,
      onDecline,
      onConsent,
      className,
      description = `
      We use cookies to enhance your experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.
      For more information, please see our [privacy policy](/privacy).`,
      ...props
    },
    ref
  ) => {
    const [isOpen, setOpen] = useState<boolean | undefined>(undefined);
    const [hide, setHide] = useState<boolean | undefined>(undefined);
    const [variant, setVariant] = useState<"default" | "small">(initialVariant);

    const changeListener = useCallback(
      (value: string | undefined, setCookieConsent: (value: string | undefined) => void) => {
        setCookieConsent(value);
        setOpen(value !== "false");
        if (value && value !== "false") {
          setOpen(false);
          setTimeout(() => {
            setHide(true);
          }, 700);
        } else {
          setHide(false);
        }
      },
      [setOpen, setHide]
    );

    const [cookieConsent, setCookieConsent] = useCookie("cookieConsent", {
      expires: 365,
      changeListener
    });

    const handleAccept = useCallback(() => {
      setOpen(false);
      setCookieConsent(new Date().toISOString());
      setTimeout(() => {
        setHide(true);
      }, 700);
      onAccept && onAccept();
    }, [onAccept, setCookieConsent]);

    const handleDecline = useCallback(() => {
      setOpen(false);
      setCookieConsent("false");
      setTimeout(() => {
        setHide(true);
      }, 700);
      onDecline && onDecline();
    }, [onDecline, setCookieConsent]);

    const handleMore = useCallback(() => {
      setVariant("default");
    }, []);

    useEffect(() => {
      try {
        setOpen(cookieConsent !== "false");
        if (cookieConsent && cookieConsent !== "false") {
          onConsent && onConsent(new Date(cookieConsent));
          setOpen(false);
          setTimeout(() => {
            setHide(true);
          }, 700);
        } else if (cookieConsent === "false") {
          onConsent && onConsent(false);
        }
      } catch (error) {
        logger.warn("Cookie consent error:", error);
      }
    }, [cookieConsent, setCookieConsent, onConsent]);

    if (hide) {
      return null;
    }

    const containerClasses = cn(
      "fixed z-50 transition-all duration-700",
      !isOpen ? "translate-y-full opacity-0" : "translate-y-0 opacity-100",
      className
    );

    const commonWrapperProps = {
      ref,
      className: cn(
        containerClasses,
        "bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 w-full sm:max-w-md"
      ),
      ...props
    };

    if (variant === "default") {
      return (
        <div {...commonWrapperProps}>
          <Card className="m-3 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg">We value your privacy</CardTitle>
              <Cookie className="h-5 w-5" />
            </CardHeader>
            <CardContent className="space-y-2">
              <CardDescription className="text-sm">
                <p>{formatMarkdown(description)}</p>
                <p className="text-sm">
                  We use essential cookies in accordance with GPDR and CCPA to understand
                  usage, and offer personalized ads or content. We retain cookies for 12 months
                  or until you withdraw consent. You can change cookie settings anytime by
                  visiting our{" "}
                  <Link href="/privacy" className="underline">
                    privacy policy
                  </Link>
                  .
                </p>
              </CardDescription>
            </CardContent>
            <CardFooter className="flex gap-2 pt-2">
              <Button onClick={handleDecline} variant="secondary" className="flex-1">
                Decline
              </Button>
              <Button onClick={handleAccept} className="flex-1">
                Accept
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    } else if (variant === "small") {
      return (
        <div {...commonWrapperProps}>
          <Card className="m-3 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 h-0 px-4">
              <CardTitle className="text-base">We value your privacy</CardTitle>
              <Cookie className="h-4 w-4" />
            </CardHeader>
            <CardContent className="pt-0 pb-2 px-4">
              <CardDescription className="text-sm">
                {formatMarkdown(description)}
              </CardDescription>
            </CardContent>
            <CardFooter className="flex gap-2 h-0 py-2 px-4">
              <Button
                onClick={handleMore}
                variant="secondary"
                size="sm"
                className="flex-1 rounded-full"
              >
                Learn more
              </Button>
              <Button onClick={handleAccept} size="sm" className="flex-1 rounded-full">
                Accept
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    } else {
      logger.warn("Unknown variant", variant);
      return null;
    }
  }
);

AnalyticsConsent.displayName = "AnalyticsConsent";
